<script setup>
import { computed, ref } from 'vue'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import { QExpansionItem } from 'quasar'
import { getI18n } from '@/plugins/i18n'
import { usePortalStore } from '@/stores/portal.store'
import { useLookupModelMap } from '@/composables/registry/import-export-lookup'
import { areMappingsComplete, getMappingLayout, getMappingSections, mergeMappingChanges } from '@/composables/registry/import-field-mapping'
import ImportFieldValueEditor from '@/components/registry/ImportFieldValueEditor.vue'

const props = defineProps({
  fields: { type: Array, required: true },
  mappings: { type: Array, required: true },
  columns: { type: Array, required: true },
  trackType: { type: Number, required: true },
})
const emit = defineEmits(['update:mappings'])
const i18n = getI18n()
const portalStore = usePortalStore()
const { getEditorOptionsForField } = useLookupModelMap()
const grids = new Map()
const pendingChanges = ref({})
const expandedSections = ref(['main'])
const fieldsByKey = computed(() => Object.fromEntries(props.fields.map(field => [field.key, field])))
const sections = computed(() => getMappingSections(props.mappings, getMappingLayout(props.trackType, portalStore.trackTypeEnum), i18n.t))
const effectiveMappings = computed(() => mergeMappingChanges(props.mappings, pendingChanges.value))
const mappingsByKey = computed(() => Object.fromEntries(effectiveMappings.value.map(row => [row.arco_field_key, row])))
const isComplete = computed(() => areMappingsComplete(effectiveMappings.value))

const onOptionChanged = (key, event) => {
  if (event.name === 'editing') {
    pendingChanges.value[key] = event.component.option('editing.changes') || []
  }
}

const onRowUpdated = (event) => {
  emit('update:mappings', props.mappings.map(row => row.arco_field_key === event.key ? { ...row, ...event.data } : row))
}

const setSectionExpanded = async(section, expanded) => {
  if (!expanded) await saveEditData(section.groups.map(group => group.key))
  expandedSections.value = expanded ? [...expandedSections.value, section.key] : expandedSections.value.filter(key => key !== section.key)
}

const updateSectionDimensions = (section) => {
  section.groups.forEach(group => grids.get(group.key)?.updateDimensions())
}

const saveEditData = (keys = [...grids.keys()]) => Promise.all(keys.map(key => grids.get(key)?.saveEditData()))

const calculateDisplayValue = (row, dataField) => {
  const options = getEditorOptionsForField(fieldsByKey.value[row.arco_field_key])
  const item = options?.dataSource?.find(item => item[options.valueExpr] === row[dataField])
  if (!item) return row[dataField]
  return typeof options.displayExpr === 'function' ? options.displayExpr(item) : item[options.displayExpr]
}

const gridOptions = computed(() => ({
  keyExpr: 'arco_field_key',
  height: 'auto',
  width: '100%',
  showBorders: true,
  showColumnLines: true,
  allowColumnResizing: true,
  columnResizingMode: 'widget',
  columnFixing: { enabled: true },
  remoteOperations: false,
  focusedRowEnabled: false,
  toolbar: { visible: false },
  columnChooser: { enabled: false },
  filterRow: { visible: false },
  sorting: { mode: 'none' },
  scrolling: { mode: 'standard' },
  paging: { enabled: false },
  pager: { visible: false },
  editing: { allowUpdating: true, mode: 'cell' },
  columns: [
    { dataField: 'label', caption: i18n.t('common.arcoField'), allowEditing: false, cellTemplate: 'labelCell' },
    {
      dataField: 'csv_column_index',
      caption: i18n.t('common.csvColumn'),
      showEditorAlways: true,
      editorOptions: { showClearButton: true, searchEnabled: true },
      setCellValue: (row, value) => {
        row.csv_column_index = value
        if (value != null) row.fixed_value = null
      },
      lookup: { dataSource: props.columns, valueExpr: 'index', displayExpr: 'name' },
    },
    ...[
      { dataField: 'fixed_value', caption: i18n.t('common.fixedValue') },
      { dataField: 'default_value', caption: i18n.t('common.defaultValue') },
    ].map(column => ({
      ...column,
      showEditorAlways: true,
      editCellTemplate: 'valueEditor',
      calculateDisplayValue: row => calculateDisplayValue(row, column.dataField),
    })),
  ],
}))

defineExpose({ saveEditData, isComplete })
</script>

<template>
  <div class="mapping-sections">
    <q-expansion-item
      v-for="section in sections"
      :key="section.key"
      :label="section.title || ''"
      :model-value="!section.title || expandedSections.includes(section.key)"
      :header-class="section.title ? 'text-subtitle1 text-weight-bold' : 'hidden'"
      class="q-mb-md"
      dense
      @update:model-value="setSectionExpanded(section, $event)"
      @after-show="updateSectionDimensions(section)"
    >
      <div :class="section.title ? 'q-px-md q-pt-sm' : ''">
        <div
          v-if="section.key === 'main' && trackType === portalStore.trackTypeEnum.VEHICLE_TRANSIT"
          class="text-body2 text-grey-7 q-mb-md"
        >
          {{ i18n.t('common.vinOrPlateRequired') }}
        </div>
        <div
          v-if="section.key === 'owner'"
          class="text-body2 text-grey-7 q-mb-md"
        >
          {{ i18n.t('common.importOwnerSearchHelp') }}
        </div>
        <div
          v-for="group in section.groups"
          :key="group.key"
          class="q-mb-lg"
        >
          <h6
            v-if="group.title"
            class="mapping-group-title"
          >
            {{ group.title }}
          </h6>
          <dx-data-grid
            v-bind="gridOptions"
            :data-source="group.rows"
            @initialized="event => grids.set(group.key, event.component)"
            @disposing="() => grids.delete(group.key)"
            @option-changed="onOptionChanged(group.key, $event)"
            @row-updated="onRowUpdated"
          >
            <template #labelCell="{ data: cellInfo }">
              <span :class="{ 'text-weight-bold': cellInfo.data.required }">
                {{ cellInfo.data.label }}{{ cellInfo.data.required ? ' *' : '' }}
              </span>
            </template>
            <template #valueEditor="{ data: cellInfo }">
              <import-field-value-editor
                :field="fieldsByKey[cellInfo.data.arco_field_key]"
                :cell-info="cellInfo"
                :disabled="cellInfo.column.dataField === 'fixed_value' && mappingsByKey[cellInfo.data.arco_field_key].csv_column_index != null"
              />
            </template>
          </dx-data-grid>
        </div>
      </div>
    </q-expansion-item>
  </div>
</template>

<style scoped>
.mapping-sections {
  overflow: auto;
}
.mapping-group-title {
  margin: 0;
  padding: 6px 0 4px;
  color: #757575;
  font-size: 15px;
  font-weight: 600;
}
</style>
